import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaTicketAlt, 
  FaUsers, 
  FaChartLine,
  FaCalendarAlt,
  FaLink,
  FaMoneyBillWave,
  FaCog
} from 'react-icons/fa';
import { motion } from 'framer-motion';

// Styled components
const Container = styled.div`
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const StyledTabs = styled(Tabs)`
  margin-bottom: 2rem;
  
  .MuiTab-root {
    color: rgba(255, 255, 255, 0.7);
    
    &.Mui-selected {
      color: #4b7bec;
    }
  }
  
  .MuiTabs-indicator {
    background-color: #4b7bec;
  }
`;

const EventCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(75, 123, 236, 0.5);
    transform: translateY(-5px);
  }
`;

const EventHeader = styled.div`
  display: flex;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EventImage = styled.img`
  width: 150px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventTitle = styled.h3`
  color: white;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EventMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  
  svg {
    color: #4b7bec;
  }
`;

const EventActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    margin-top: 1rem;
    justify-content: flex-end;
  }
`;

const EventBody = styled.div`
  padding: 1rem;
`;

const EventStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: #4b7bec;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const NoEventsMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.5);
  
  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: rgba(75, 123, 236, 0.5);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
`;

const StyledTableCell = styled(TableCell)`
  color: white !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
`;

const StyledTableHeadCell = styled(TableCell)`
  color: #4b7bec !important;
  font-weight: 600 !important;
  border-bottom: 1px solid rgba(75, 123, 236, 0.3) !important;
`;

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const EventManagementDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Fetch events created by the current user
  useEffect(() => {
    const fetchEvents = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Query events created by the current user
        const eventsQuery = query(
          collection(db, 'streamEvents'),
          where('creatorId', '==', currentUser.uid),
          orderBy('date', 'desc')
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsList = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Fetch tickets for each event
        const ticketsPromises = eventsList.map(async (event) => {
          const ticketsQuery = query(
            collection(db, 'tickets'),
            where('eventId', '==', event.id)
          );
          
          const ticketsSnapshot = await getDocs(ticketsQuery);
          return ticketsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        });
        
        const ticketsResults = await Promise.all(ticketsPromises);
        const allTickets = ticketsResults.flat();
        
        // Calculate stats for each event
        const eventsWithStats = eventsList.map(event => {
          const eventTickets = allTickets.filter(ticket => ticket.eventId === event.id);
          const totalSales = eventTickets.reduce((sum, ticket) => sum + (ticket.price || 0), 0);
          const activeTickets = eventTickets.filter(ticket => ticket.status === 'active').length;
          const pendingTickets = eventTickets.filter(ticket => ticket.status === 'pending').length;
          
          return {
            ...event,
            stats: {
              totalTickets: eventTickets.length,
              activeTickets,
              pendingTickets,
              totalSales
            }
          };
        });
        
        setEvents(eventsWithStats);
        setTickets(allTickets);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, [currentUser]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleCreateEvent = () => {
    navigate('/streaming-events/create-event');
  };
  
  const handleEditEvent = (event) => {
    navigate(`/streaming-events/edit-event/${event.id}`);
  };
  
  const handleViewEvent = (event) => {
    navigate(`/streaming-events/${event.id}`);
  };
  
  const handleDeleteClick = (event) => {
    setSelectedEvent(event);
    setShowDeleteDialog(true);
  };
  
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      setDeleteLoading(true);
      
      // Delete the event
      await deleteDoc(doc(db, 'streamEvents', selectedEvent.id));
      
      // Update the events list
      setEvents(events.filter(event => event.id !== selectedEvent.id));
      
      setShowDeleteDialog(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get event status
  const getEventStatus = (event) => {
    const eventDate = new Date(event.date + 'T' + event.time);
    const now = new Date();
    
    if (eventDate > now) {
      return { label: 'Upcoming', color: '#4b7bec' };
    } else {
      return { label: 'Past', color: '#a0a0a0' };
    }
  };
  
  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress style={{ color: '#4b7bec' }} />
      </LoadingContainer>
    );
  }
  
  return (
    <>
      <Container>
        <Title>
          <FaCog /> Event Management
        </Title>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <StyledTabs value={tabValue} onChange={handleTabChange}>
            <Tab label="My Events" />
            <Tab label="Tickets" />
            <Tab label="Analytics" />
          </StyledTabs>
          
          <Button
            variant="contained"
            startIcon={<FaCalendarAlt />}
            onClick={handleCreateEvent}
            sx={{
              backgroundColor: '#4b7bec',
              '&:hover': { backgroundColor: '#3867d6' }
            }}
          >
            Create Event
          </Button>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          {events.length === 0 ? (
            <NoEventsMessage>
              <FaCalendarAlt />
              <Typography>You haven't created any events yet.</Typography>
              <Button
                variant="contained"
                onClick={handleCreateEvent}
                sx={{
                  mt: 2,
                  backgroundColor: '#4b7bec',
                  '&:hover': { backgroundColor: '#3867d6' }
                }}
              >
                Create Your First Event
              </Button>
            </NoEventsMessage>
          ) : (
            events.map(event => {
              const status = getEventStatus(event);
              
              return (
                <EventCard
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <EventHeader>
                    <EventImage src={event.image} alt={event.title} />
                    <EventInfo>
                      <EventTitle>
                        {event.title}
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: status.color,
                            borderColor: status.color,
                            ml: 1
                          }}
                          variant="outlined"
                        />
                      </EventTitle>
                      <EventMeta>
                        <MetaItem>
                          <FaCalendarAlt />
                          <span>{formatDate(event.date)}</span>
                        </MetaItem>
                        <MetaItem>
                          <FaTicketAlt />
                          <span>${event.price}</span>
                        </MetaItem>
                        <MetaItem>
                          <FaUsers />
                          <span>{event.stats.totalTickets} tickets sold</span>
                        </MetaItem>
                        <MetaItem>
                          <FaMoneyBillWave />
                          <span>${event.stats.totalSales}</span>
                        </MetaItem>
                      </EventMeta>
                    </EventInfo>
                    <EventActions>
                      <Tooltip title="View Event">
                        <IconButton
                          onClick={() => handleViewEvent(event)}
                          sx={{ color: 'white' }}
                        >
                          <FaEye />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Event">
                        <IconButton
                          onClick={() => handleEditEvent(event)}
                          sx={{ color: '#4b7bec' }}
                        >
                          <FaEdit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Event">
                        <IconButton
                          onClick={() => handleDeleteClick(event)}
                          sx={{ color: '#e74c3c' }}
                        >
                          <FaTrash />
                        </IconButton>
                      </Tooltip>
                    </EventActions>
                  </EventHeader>
                  <EventBody>
                    <EventStats>
                      <StatCard>
                        <StatValue>{event.stats.totalTickets}</StatValue>
                        <StatLabel>Total Tickets</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>{event.stats.activeTickets}</StatValue>
                        <StatLabel>Active Tickets</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>{event.stats.pendingTickets}</StatValue>
                        <StatLabel>Pending Tickets</StatLabel>
                      </StatCard>
                      <StatCard>
                        <StatValue>${event.stats.totalSales}</StatValue>
                        <StatLabel>Total Revenue</StatLabel>
                      </StatCard>
                    </EventStats>
                  </EventBody>
                </EventCard>
              );
            })
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell>Ticket ID</StyledTableHeadCell>
                  <StyledTableHeadCell>Event</StyledTableHeadCell>
                  <StyledTableHeadCell>Customer</StyledTableHeadCell>
                  <StyledTableHeadCell>Type</StyledTableHeadCell>
                  <StyledTableHeadCell>Price</StyledTableHeadCell>
                  <StyledTableHeadCell>Status</StyledTableHeadCell>
                  <StyledTableHeadCell>Purchase Date</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={7} align="center">
                      No tickets sold yet
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  tickets
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(ticket => {
                      const event = events.find(e => e.id === ticket.eventId);
                      return (
                        <TableRow key={ticket.id}>
                          <StyledTableCell>{ticket.id.slice(0, 8)}...</StyledTableCell>
                          <StyledTableCell>{event ? event.title : 'Unknown Event'}</StyledTableCell>
                          <StyledTableCell>
                            {ticket.contactInfo?.name || 'Anonymous'}
                            <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                              {ticket.contactInfo?.email || 'No email'}
                            </div>
                          </StyledTableCell>
                          <StyledTableCell>
                            {ticket.ticketType === 'premium' ? (
                              <Chip
                                label="Premium"
                                size="small"
                                sx={{ backgroundColor: 'rgba(155, 89, 182, 0.2)', color: '#9b59b6' }}
                              />
                            ) : (
                              <Chip
                                label="Standard"
                                size="small"
                                sx={{ backgroundColor: 'rgba(75, 123, 236, 0.2)', color: '#4b7bec' }}
                              />
                            )}
                          </StyledTableCell>
                          <StyledTableCell>${ticket.price}</StyledTableCell>
                          <StyledTableCell>
                            {ticket.status === 'active' ? (
                              <Chip
                                label="Active"
                                size="small"
                                sx={{ backgroundColor: 'rgba(46, 204, 113, 0.2)', color: '#2ecc71' }}
                              />
                            ) : ticket.status === 'pending' ? (
                              <Chip
                                label="Pending"
                                size="small"
                                sx={{ backgroundColor: 'rgba(243, 156, 18, 0.2)', color: '#f39c12' }}
                              />
                            ) : (
                              <Chip
                                label="Failed"
                                size="small"
                                sx={{ backgroundColor: 'rgba(231, 76, 60, 0.2)', color: '#e74c3c' }}
                              />
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {ticket.purchaseDate?.toDate ? 
                              ticket.purchaseDate.toDate().toLocaleDateString() : 
                              'Unknown'}
                          </StyledTableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={tickets.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ color: 'white' }}
            />
          </TableContainer>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <FaChartLine style={{ fontSize: '3rem', color: '#4b7bec', marginBottom: '1rem' }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
              Analytics Dashboard
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Detailed analytics for your events will be available soon.
            </Typography>
          </Box>
        </TabPanel>
      </Container>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        PaperProps={{
          style: {
            backgroundColor: '#1a1e2e',
            color: 'white',
            borderRadius: '12px',
            padding: '1rem'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          Delete Event
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the event "{selectedEvent?.title}"? This action cannot be undone.
          </Typography>
          <Typography sx={{ mt: 2, color: '#e74c3c' }}>
            Warning: All associated tickets will also be invalidated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEvent}
            disabled={deleteLoading}
            sx={{
              backgroundColor: '#e74c3c',
              color: 'white',
              '&:hover': { backgroundColor: '#c0392b' }
            }}
            variant="contained"
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventManagementDashboard;
